import React from "react";
import { Dna, TrendingUp, DollarSign, Wrench, Shield, FileCheck, Sparkles, ArrowRight } from "lucide-react";

/**
 * FourPillars section — the marketing-site expression of the same
 * 4-pillar reorganization the /app now uses. Positions EVIQ as an
 * intelligence platform, not a battery-health app.
 */
const PILLARS = [
  {
    id: "battery",
    icon: Dna,
    accent: "#39FF14",
    eyebrow: "Pillar 01",
    title: "Battery Intelligence",
    sub: "Eight measurable signals, all traceable to your car's own telemetry.",
    points: [
      "Capacity Retention · Degradation Velocity",
      "Thermal Stress · Charging Quality",
      "Calibration Confidence · Battery Stability",
      "Future Health Forecast · Resale Value",
      "Warranty Risk",
    ],
  },
  {
    id: "predict",
    icon: TrendingUp,
    accent: "#00E5FF",
    eyebrow: "Pillar 02",
    title: "Predictive Intelligence",
    sub: "Answers, not data.",
    points: [
      "8-year SoH forecast against warranty floor",
      "Digital Twin — what-if simulator (5 toggles)",
      "\"Reduce DC charging by 20% → +0.8% saved SoH\"",
      "Fleet-relative aging (\"slower than 88% of similar packs\")",
    ],
  },
  {
    id: "fi",
    icon: DollarSign,
    accent: "#39FF14",
    eyebrow: "Pillar 03",
    title: "Financial Intelligence",
    sub: "Battery value is a lever, not a mystery.",
    points: [
      "Battery Value · vehicle contribution %",
      "Resale premium · expected warranty exposure",
      "Projected replacement cost · insurance risk",
      "Depreciation projection (next 12 months)",
    ],
  },
  {
    id: "care",
    icon: Wrench,
    accent: "#7FD1FF",
    eyebrow: "Pillar 04",
    title: "Ownership Intelligence",
    sub: "The one-place-for-everything layer.",
    points: [
      "Live-odometer service timeline",
      "Warranty Assistant · readiness checklist",
      "Recall + firmware timeline · accessory recs",
      "(EVIQ never claims warranty qualification — we surface signals.)",
    ],
  },
];

/**
 * SPOTLIGHTS — the three flagship consumer artifacts that anchor the
 * pillars: Battery DNA (screen), Passport (public share), Certification (PDF).
 */
const SPOTLIGHTS = [
  {
    id: "dna",
    icon: Dna,
    tag: "Battery Health Profile",
    title: "Nine dimensions. One screen.",
    body: "Health · Degradation slope · Thermal · Charging · Efficiency · Predicted future · Risk · Fleet rank · Warranty headroom. Everything about your pack on one screen.",
    accent: "#39FF14",
  },
  {
    id: "passport",
    icon: Shield,
    tag: "Digital Battery Passport™",
    title: "Verified record. Shareable at sale.",
    body: "Every pack gets a tamper-evident, QR-verifiable public record. Share it with private-party buyers. Renew it every 90 days.",
    accent: "#00E5FF",
  },
  {
    id: "cert",
    icon: FileCheck,
    tag: "EVIQ Certified Report™",
    title: "Buyers trust it. Dealers rely on it.",
    body: "1-page PDF report with full methodology, tamper-resistant Report ID, and QR verification. Standard for private-party sale and dealer pre-inspection.",
    accent: "#FFB020",
  },
];

export const FourPillars = () => {
  return (
    <section
      id="four-pillars"
      className="relative py-20 sm:py-28 border-t border-white/5"
      style={{
        background:
          "radial-gradient(ellipse at 50% 0%, rgba(0,229,255,0.06), transparent 55%), #060606",
      }}
      data-testid="section-four-pillars"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        {/* Eyebrow */}
        <div className="text-center mb-4">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1"
            style={{ borderColor: "rgba(0,229,255,0.35)", background: "rgba(0,229,255,0.08)" }}
          >
            <Sparkles className="w-3 h-3" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#00E5FF" }}>
              The Four Pillars of EVIQ
            </span>
          </span>
        </div>

        {/* Section title */}
        <h2 className="text-center font-display text-4xl sm:text-5xl lg:text-6xl text-white font-semibold leading-tight">
          Not a battery health app.
          <br />
          <span className="brand-text-gradient">The intelligence layer for EV ownership.</span>
        </h2>
        <p className="mt-4 text-center text-zinc-400 text-base leading-relaxed max-w-2xl mx-auto">
          EVIQ organizes everything about your pack into four pillars — mapped
          into a redesigned mobile app that mirrors how you actually think
          about your EV.
        </p>

        {/* Pillars grid */}
        <div className="mt-14 grid gap-4 md:grid-cols-2" data-testid="pillars-grid">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                className="rounded-2xl border p-6 relative overflow-hidden"
                style={{
                  borderColor: `${p.accent}33`,
                  background: `linear-gradient(160deg, ${p.accent}0a 0%, transparent 60%)`,
                }}
                data-testid={`pillar-${p.id}`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center border shrink-0"
                    style={{
                      background: `${p.accent}18`,
                      borderColor: `${p.accent}55`,
                    }}
                  >
                    <Icon className="w-5 h-5" style={{ color: p.accent }} strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                      {p.eyebrow}
                    </div>
                    <h3 className="font-display text-xl sm:text-2xl text-white font-semibold leading-tight mt-1">
                      {p.title}
                    </h3>
                    <p className="text-[13px] font-medium mt-1" style={{ color: p.accent }}>
                      {p.sub}
                    </p>
                  </div>
                </div>
                <ul className="mt-5 space-y-1.5">
                  {p.points.map((pt, i) => (
                    <li key={i} className="text-[13px] text-zinc-300 leading-relaxed flex items-start gap-2">
                      <span
                        className="w-1 h-1 rounded-full mt-2 shrink-0"
                        style={{ background: p.accent }}
                      />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Spotlight — 3 flagship artifacts */}
        <div className="mt-16 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
            The three artifacts that make EVIQ different
          </div>
          <h3 className="font-display text-3xl sm:text-4xl text-white font-semibold leading-tight">
            Health Profile · Passport · Certification
          </h3>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3" data-testid="spotlights-grid">
          {SPOTLIGHTS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.id}
                className="rounded-2xl border p-5"
                style={{
                  borderColor: `${s.accent}44`,
                  background: `linear-gradient(180deg, ${s.accent}0d 0%, transparent 70%)`,
                }}
                data-testid={`spotlight-${s.id}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center border mb-3"
                  style={{
                    background: `${s.accent}18`,
                    borderColor: `${s.accent}66`,
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: s.accent }} strokeWidth={2.2} />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: s.accent }}>
                  {s.tag}
                </div>
                <h4 className="font-display text-lg text-white font-semibold leading-tight mt-1">
                  {s.title}
                </h4>
                <p className="text-[13px] text-zinc-400 leading-relaxed mt-2">
                  {s.body}
                </p>
              </div>
            );
          })}
        </div>

        {/* Live-preview CTA — link to demo passport route */}
        <div className="mt-6 text-center" data-testid="pillars-demo-passport-row">
          <a
            href="/passport/demo"
            className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[12px] font-mono uppercase tracking-wider text-white hover:bg-white/[0.04] transition-colors"
            style={{
              borderColor: "rgba(0,229,255,0.4)",
              background: "rgba(0,229,255,0.06)",
            }}
            data-testid="pillars-see-real-passport"
          >
            <Shield className="w-3.5 h-3.5" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
            <span>See a real Battery Passport</span>
            <ArrowRight className="w-3.5 h-3.5" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
          </a>
          <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Example · no vehicle connection required
          </div>
        </div>

        {/* Vision statement */}
        <div className="mt-16 max-w-3xl mx-auto text-center">
          <blockquote
            className="rounded-2xl border p-6 sm:p-8"
            style={{
              borderColor: "rgba(255,255,255,0.10)",
              background: "rgba(255,255,255,0.02)",
            }}
            data-testid="pillars-vision"
          >
            <p className="text-lg sm:text-xl text-white font-medium leading-relaxed italic">
              &ldquo;EVIQ is the intelligence layer for electric vehicle
              ownership — transforming raw vehicle data into trusted
              insights about battery condition, future performance,
              ownership costs, and long-term value.&rdquo;
            </p>
            <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              EVIQ · vision
            </div>
          </blockquote>
        </div>

        {/* CTA row */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="/app"
            className="brand-gradient text-black font-semibold rounded-2xl px-6 py-3 text-sm inline-flex items-center gap-2 hover:opacity-90 transition-opacity glow-green"
            data-testid="pillars-cta-open-app"
          >
            Open EVIQ
            <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
          </a>
          <a
            href="#pricing"
            className="rounded-2xl border px-6 py-3 text-sm text-white font-semibold hover:bg-white/5 transition-colors"
            style={{ borderColor: "rgba(255,255,255,0.15)" }}
            data-testid="pillars-cta-pricing"
          >
            See pricing
          </a>
        </div>
      </div>
    </section>
  );
};
